import { useCallback, useEffect, useState } from "react";
import { defineMessages } from "react-intl";

import { useFormatMessage } from "../Intl/Context";
import Button from "../Buttons/Button";
import SpeechToTextRecorder from "../SpeechToText/SpeechToTextRecorder";
import InterviewStateCard from "./InterviewStateCard";

// The microphone check stays local: its transcription only confirms that recording
// works and must never be persisted as an interview response. Passing the mic test is
// the mandatory gate before a participant can answer by voice, and playing the take
// back doubles as a speaker test. Reused both before the interview and when a written
// participant asks to switch to voice.
const InterviewMicroCheckSection = ({
  resourceId,
  onMicCheckPassed,
  onContinueInWriting,
  isBusy
}) => {
  const t = useFormatMessage();
  const [transcription, setTranscription] = useState("");
  const [recordingUrl, setRecordingUrl] = useState(null);
  const hasReviewableTranscription = transcription.trim().length > 0;
  const title = t(intlMessages.microCheckTitle);
  const description = t(intlMessages.microCheckDescription);
  const transcriptionLabel = t(intlMessages.transcriptionLabel);
  const playbackQuestion = t(intlMessages.playbackQuestion);
  const retryLabel = t(intlMessages.retryMicroCheck);
  const confirmLabel = t(intlMessages.confirmRecordingHeard);
  const continueInWritingLabel = t(intlMessages.continueInWriting);

  const handleRetryMicroCheck = useCallback(() => {
    setTranscription("");
    setRecordingUrl(null);
  }, []);

  const handleRecorderResult = useCallback(nextTranscription => {
    const normalizedTranscription = (nextTranscription || "").trim();
    if (!normalizedTranscription) return;

    setTranscription(normalizedTranscription);
  }, []);

  // Revokes the previous take when a new one arrives and on unmount, so replayed
  // recordings never leak object URLs.
  useEffect(() => {
    if (!recordingUrl) return undefined;

    return () => URL.revokeObjectURL(recordingUrl);
  }, [recordingUrl]);

  return (
    <InterviewStateCard state="idle">
      <div className="oral-interview-micro-check">
        <div className="oral-interview-response-header margin-bottom-sm">
          <div>
            <h4 className="oral-interview-micro-check__title">{title}</h4>

            <p className="margin-none">{description}</p>
          </div>

          {/* The recorder renders nothing when the browser doesn't support it or
              the microphone is denied, so the interview must stay reachable in
              writing. */}
          {!hasReviewableTranscription && (
            <Button
              type="button"
              className="btn btn-primary-inverse btn-shadow"
              onClick={onContinueInWriting}
              disabled={isBusy}
              label={continueInWritingLabel}
              iconClassName="fa fa-keyboard"
            />
          )}
        </div>

        {!hasReviewableTranscription && (
          <SpeechToTextRecorder
            resourceId={resourceId}
            onResult={handleRecorderResult}
            onRecordingReady={setRecordingUrl}
            onCancel={handleRetryMicroCheck}
            disabled={isBusy}
          />
        )}

        {hasReviewableTranscription && (
          <>
            <div className="oral-interview-transcription-review margin-bottom-sm">
              <p className="text-legend margin-bottom-xxs">{transcriptionLabel}</p>

              <div
                className="form-control oral-interview-transcription-edit"
                aria-readonly="true"
              >
                {transcription}
              </div>
            </div>

            {recordingUrl && (
              <audio
                className="oral-interview-recording-playback margin-bottom-sm"
                src={recordingUrl}
                controls
              />
            )}

            <p className="margin-bottom-sm">{playbackQuestion}</p>

            <Button
              type="button"
              className="btn btn-primary-inverse margin-right-xxs"
              onClick={handleRetryMicroCheck}
              disabled={isBusy}
              label={retryLabel}
            />

            <Button
              type="button"
              className="btn btn-primary oral-interview-primary-cta"
              onClick={onMicCheckPassed}
              disabled={isBusy}
              label={confirmLabel}
            />
          </>
        )}
      </div>
    </InterviewStateCard>
  );
};

export default InterviewMicroCheckSection;

const intlMessages = defineMessages({
  confirmRecordingHeard: {
    id: "answer.oralInterview.confirmRecordingHeard",
    defaultMessage: "Oui, j'ai bien entendu"
  },
  continueInWriting: {
    id: "answer.oralInterview.continueInWriting",
    defaultMessage: "Continuer par écrit"
  },
  microCheckDescription: {
    id: "answer.oralInterview.microCheckDescription",
    defaultMessage:
      "Testez le micro et le son de votre appareil : prononcez " +
      "une courte phrase, puis écoutez votre enregistrement."
  },
  microCheckTitle: {
    id: "answer.oralInterview.microCheckTitle",
    defaultMessage: "Vérification avant entretien"
  },
  playbackQuestion: {
    id: "answer.oralInterview.playbackQuestion",
    defaultMessage: "Avez-vous bien entendu votre enregistrement ?"
  },
  retryMicroCheck: {
    id: "answer.oralInterview.retryMicroCheck",
    defaultMessage: "Réessayer"
  },
  transcriptionLabel: {
    id: "answer.oralInterview.microCheckTranscriptionLabel",
    defaultMessage: "Votre réponse :"
  }
});
